import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { CheckCircle, XCircle, Clock, Mail } from 'lucide-react';

export function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState<{ email: string; userName: string } | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Μη έγκυρο token επιβεβαίωσης');
      return;
    }

    verifyEmail(token);
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      const response = await apiClient.get(`/auth?action=verify-email&token=${verificationToken}`);
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        setUserData(response.data.data);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Το email σας επιβεβαιώθηκε! Μπορείτε τώρα να συνδεθείτε.',
              type: 'success' 
            }
          });
        }, 3000);
      } else {
        setStatus('error');
        setMessage(response.data.message);
      }
    } catch (error: any) {
      console.error('Email verification error:', error);
      
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('λήξει')) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
        
        setMessage(errorMessage);
      } else {
        setStatus('error');
        setMessage('Σφάλμα κατά την επιβεβαίωση του email');
      }
    }
  };

  const resendVerificationEmail = async () => {
    if (!userData?.email) return;

    try {
      const response = await apiClient.post('/auth?action=resend-verification', {
        email: userData.email
      });

      if (response.data.success) {
        setMessage('Νέο email επιβεβαίωσης στάλθηκε! Ελέγξτε τα εισερχόμενά σας.');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setMessage('Σφάλμα κατά την αποστολή νέου email επιβεβαίωσης');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-medical-50 to-clinical-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 transition-colors duration-300">
          <div className="text-center">
            {/* Header */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-medical-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Mail className="w-8 h-8 text-white" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">
              Επιβεβαίωση Email
            </h1>

            {/* Loading State */}
            {status === 'loading' && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Clock className="w-12 h-12 text-blue-500 animate-spin" />
                </div>
                <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
                  Επιβεβαίωση του email σας...
                </p>
              </div>
            )}

            {/* Success State */}
            {status === 'success' && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <div className="space-y-2">
                  <p className="text-green-600 font-medium text-lg">
                    {message}
                  </p>
                  {userData && (
                    <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
                      Καλώς ήρθατε, {userData.userName}!
                    </p>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                    Θα μεταφερθείτε στη σελίδα σύνδεσης σε λίγα δευτερόλεπτα...
                  </p>
                </div>
                
                <div className="pt-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-medical-500 to-medical-600 text-white rounded-lg hover:from-medical-600 hover:to-medical-700 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Συνδεθείτε Τώρα
                  </Link>
                </div>
              </div>
            )}

            {/* Error State */}
            {status === 'error' && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <XCircle className="w-16 h-16 text-red-500" />
                </div>
                <div className="space-y-2">
                  <p className="text-red-600 font-medium">
                    {message}
                  </p>
                </div>
                
                <div className="pt-4 space-y-2">
                  <Link
                    to="/register"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-medical-500 to-medical-600 text-white rounded-lg hover:from-medical-600 hover:to-medical-700 transition-all duration-200 shadow-md hover:shadow-lg mr-2"
                  >
                    Εγγραφή Ξανά
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    Σύνδεση
                  </Link>
                </div>
              </div>
            )}

            {/* Expired State */}
            {status === 'expired' && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Clock className="w-16 h-16 text-orange-500" />
                </div>
                <div className="space-y-2">
                  <p className="text-orange-600 font-medium">
                    {message}
                  </p>
                </div>
                
                <div className="pt-4 space-y-2">
                  {userData?.email && (
                    <button
                      onClick={resendVerificationEmail}
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-medical-500 to-medical-600 text-white rounded-lg hover:from-medical-600 hover:to-medical-700 transition-all duration-200 shadow-md hover:shadow-lg mr-2"
                    >
                      Αποστολή Νέου Email
                    </button>
                  )}
                  <Link
                    to="/login"
                    className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    Επιστροφή στη Σύνδεση
                  </Link>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                Χρειάζεστε βοήθεια;{' '}
                <a 
                  href="mailto:support@pathiety.gr" 
                  className="text-medical-600 hover:text-medical-700 dark:text-medical-400 dark:hover:text-medical-300 transition-colors duration-200"
                >
                  Επικοινωνήστε μαζί μας
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}